import { useContext } from "react";
import { useTripsCtx } from "../context/TripDetailsContext";
import { notification } from "antd";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import {
  GetPersonPersonRelationships,
  GetPersonRegistrationMatch,
  UpsertTrustedContactDetails,
  getPersonType,
  updateAdditionalDetailsForPerson,
  uploadCCPDetailsImage,
} from "../services";
import {
  GeneralDefinition,
  InsertParameter,
  PersonTypeIds,
  RegistrationEnums,
  TrackAnalytics,
} from "../types/ApplicationTypes";
import { handleGetDateOfBirth } from "../helpers/ServiceUserCreation";
import { PersonType } from "../types/PersonTypes";
import { useServiceUserCreationCtx } from "../context/ServiceUserCreationContext";
import EmailServiceUserAdded from "../helpers/EmailServiceUserAdded";
import { usePlanDetailsCtx } from "../context/PlanDetailsContext";
import { Contact } from "../components/CreatePerson/Forms/TrustedContactsForm";
import { PersonCreationData } from "../types/ServiceUserCreationTypes";
import { UploadImageResult } from "../components/EditProfileComponent/EditProfileServices";
import moment from "moment";

export function usePersonCreation() {
  const { wandaOrgId, handleManageServiceUsersData } = useTripsCtx();
  const {
    serviceUserMyselfDetailsCtx,
    serviceUserSomeoneElseDetailsCtx,
    whoWillUseServicesCtx,
    relationshipTypesCtx,
    pageTypeCtx,
    isLoadingCtx,
  } = useServiceUserCreationCtx();
  const [, setServiceUserMyselfDetails] = serviceUserMyselfDetailsCtx;
  const [, setServiceUserSomeoneElseDetails] = serviceUserSomeoneElseDetailsCtx;
  const [whoWillUseServices] = whoWillUseServicesCtx;
  const [relationshipTypes] = relationshipTypesCtx;
  const [, setPageType] = pageTypeCtx;
  const [, setIsLoading] = isLoadingCtx;
  const { tripDetailsInfo } = usePlanDetailsCtx();
  const {
    caregiverSummaryInfo,
    setCaregiverSummaryInfo,
  } = usePersonSummaryCtx();

  const isPersonMatchFound = async (values: any) => {
    try {
      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phone,
        email: values.email,
        zipCode: values.homeAddress.zip,
        dateOfBirth:
          values.dateOfBirth && moment(values.dateOfBirth).format("YYYY-MM-DD"),
        searchPattern: RegistrationEnums.PersonMatch,
      };
      let matchResult = await GetPersonRegistrationMatch(data);
      return matchResult;
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch matching record.",
      });
      setIsLoading(false);
    }
  };

  const getMatchedPersonRelCheck = async (personId: number) => {
    try {
      const serviceUserCheck = await GetPersonPersonRelationships(
        GeneralDefinition.DefaultOrganizationId,
        personId,
        GeneralDefinition.Position
      ); // Service user check- MTMPORT-9949

      const serviceUserInfo = serviceUserCheck.data.filter(
        (user: { childOrganizationId: string; active: number }) =>
          user.childOrganizationId.toString() ===
            wandaOrgId.benefitGroupId.toString() && user.active === 1
      );
      return serviceUserInfo;
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch PersonPersonRel for a matched Person.",
      });
      setIsLoading(false);
    }
  };

  const getMemberStatus = async (personId: number) => {
    try {
      const personTypes = await getPersonType(personId); //member check
      if (personTypes) {
        const isMember = personTypes.some(
          (x: { personTypeId: Number }) =>
            x.personTypeId === PersonTypeIds.member
        );
        return isMember;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to check whether person is a member.",
      });
      setIsLoading(false);
    }
  };

  const handlePersonCreationData = async (
    formValues: any,
    personType: string,
    personId: number,
    personMatchFound: boolean
  ) => {
    const serviceUserData = {
      dateOfBirth: formValues.dateOfBirth,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      gender: "N/A",
      personId,
      keyTypeId: RegistrationEnums.KeyTypeId,
      isActive: true,
      personTypeId: RegistrationEnums.PersonTypeId,
      organizationId: wandaOrgId.benefitGroupId,
      zipCode: formValues.homeAddress.zip,
      personAttributes: {
        personInfo: {
          primaryLanguage: "English",
        },
        homeAddress: {
          address1: formValues.homeAddress.address1,
          address2: formValues.homeAddress.address2 || "",
          city: formValues.homeAddress.city,
          state: formValues.homeAddress.state,
          zip: formValues.homeAddress.zip,
          county: formValues.homeAddress.county,
        },
        mailingAddress: {
          address1: formValues.mailingAddress.address1,
          address2: formValues.mailingAddress.address2 || "",
          city: formValues.mailingAddress.city,
          state: formValues.mailingAddress.state,
          zip: formValues.mailingAddress.zip,
          county: formValues.mailingAddress.county,
        },
        personContact: {
          wandaEmail: formValues.email,
          WandaEmailAddressAttribute: {
            notificationOn: true,
            communicationTypes: {
              informative: true,
              promotional:
                personType === PersonType.Myself
                  ? caregiverSummaryInfo.wandaEmailAddressAttribute
                    ? caregiverSummaryInfo.wandaEmailAddressAttribute?.communicationTypes?.promotional
                    : true
                  : true,
            },
          },
          primaryPhone: formValues.phone,
          PrimaryPhoneAttribute:
            personType === PersonType.Myself || personMatchFound
              ? undefined
              : {
                  canReceiveTexts: true,
                  notificationOn: true,
                  communicationTypes: {
                    informative: true,
                  },
                },
          wandaRelation:
            personType === PersonType.SomeoneElse
              ? formValues.relationship
              : undefined,
        },
      },
      wandaAccommodations: {
        wheelChairTravel: formValues.wandaAccommodations.wheelChairTravel,
        wheelChairPresent: formValues.wandaAccommodations.wheelChairPresent,
        wcToVehicleTransfer: formValues.wandaAccommodations.wcToVehicleTransfer,
        personAttributesKeyId: 0,
      },
    };

    const serviceUserStatiusData = {
      caregiverEmailId: caregiverSummaryInfo.wandaEmail,
      caregiverPersonId: caregiverSummaryInfo.personId,
      registeredEmail:
        personType === PersonType.SomeoneElse ? formValues.email : "",
      imageUrl: formValues.photo,
      imageKey:
        personType === PersonType.Myself ? caregiverSummaryInfo.imageKey : "",
      imageFile: formValues.photoFile,
      dobUpdated:
        handleGetDateOfBirth(formValues.dateOfBirth) ===
        RegistrationEnums.DateOfBirth
          ? false
          : true,
      personFound: personMatchFound && true,
      insertParameter:
        personType === PersonType.Myself
          ? InsertParameter.updateRecord
          : InsertParameter.InsertRecord,
      updatePersonPersonRel: true,
    };

    const PersonCreationData = {
      createPersonData: serviceUserData,
      createPersonStatusInfo: serviceUserStatiusData,
    };

    personType === PersonType.Myself
      ? setServiceUserMyselfDetails(PersonCreationData)
      : setServiceUserSomeoneElseDetails(PersonCreationData);
  };

  const handleCreatePerson = async (
    contacts: Contact[],
    serviceUserCreationDetails: PersonCreationData
  ) => {
    try {
      let personResult;
      if (!["Member", "MemberProfile"].includes(whoWillUseServices)) {
        if (["SomeoneElse", "Profile"].includes(whoWillUseServices)) {
          serviceUserCreationDetails.createPersonData.personAttributes.personContact.wandaRelation = relationshipTypes.find(
            (x: { value: number }) =>
              x.value ===
              Number(
                serviceUserCreationDetails.createPersonData.personAttributes
                  .personContact.wandaRelation
              )
          ).label;
        }

        let imageKey = {} as UploadImageResult;
        if (serviceUserCreationDetails.createPersonStatusInfo.imageFile) {
          imageKey = await uploadCCPDetailsImage(
            serviceUserCreationDetails.createPersonStatusInfo.imageFile,
            caregiverSummaryInfo.personId
          );
          serviceUserCreationDetails.createPersonStatusInfo.imageKey =
            imageKey.key;
        }

        serviceUserCreationDetails.createPersonData.dateOfBirth = handleGetDateOfBirth(
          serviceUserCreationDetails.createPersonData.dateOfBirth
        );

        personResult = await updateAdditionalDetailsForPerson(
          serviceUserCreationDetails
        );

        if (
          ["Myself", "ProfileMyself"].includes(whoWillUseServices) &&
          personResult.personId
        ) {
          setCaregiverSummaryInfo({
            ...caregiverSummaryInfo,
            dateOfBirth:
              serviceUserCreationDetails.createPersonData.dateOfBirth,
            imageUrl:
              serviceUserCreationDetails.createPersonStatusInfo.imageUrl,
            imageKey:
              serviceUserCreationDetails.createPersonStatusInfo.imageKey,
            homeAddress:
              serviceUserCreationDetails.createPersonData.personAttributes
                .homeAddress,
            mailingAddress:
              serviceUserCreationDetails.createPersonData.personAttributes
                .mailingAddress,
          });
        }
      }

      if (
        personResult?.personId ||
        ["Member", "MemberProfile"].includes(whoWillUseServices)
      ) {
        if (contacts.length > 0) {
          const tcData = contacts.map((contact: any, index: number) => ({
            firstName: contact.firstName,
            lastName: contact.lastName,
            phone: contact.phone.replace(/-/g, ""),
            phoneAttributes: {
              canReceiveTexts: true,
              notificationOn: true,
              communicationTypes: {
                informative: true,
              },
            },
            email: contact.email,
            emailAttributes: {
              notificationOn: true,
              communicationTypes: {
                informative: true,
              },
            },
            relationship: Number(contact.relationship),
            priority: index,
            personAttributesKeyId: 0,
            isActive: true,
          }));
          const trustedContactCreationData = {
            personId: ["Member", "MemberProfile"].includes(whoWillUseServices)
              ? caregiverSummaryInfo.personId
              : personResult.personId,
            trustedContact: tcData,
          };
          await UpsertTrustedContactDetails(trustedContactCreationData);
          TrackAnalytics(
            "/DashBoard",
            "Trusted Contact added",
            "Trusted Contact Page"
          );
        }

        await EmailServiceUserAdded(
          caregiverSummaryInfo.personId,
          wandaOrgId.privatePayId,
          caregiverSummaryInfo.firstName,
          ["Member", "MemberProfile"].includes(whoWillUseServices)
            ? caregiverSummaryInfo.personId
            : personResult.personId,
          tripDetailsInfo.wandaCustomerCareNumber
        );

        const result = await handleManageServiceUsersData();
        setPageType("");
        setServiceUserMyselfDetails();
        setServiceUserSomeoneElseDetails();
        setIsLoading(false);

        return result;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to add Service User. Please try again later.",
      });
      setIsLoading(false);
    }
  };

  return {
    isPersonMatchFound,
    getMatchedPersonRelCheck,
    getMemberStatus,
    handlePersonCreationData,
    handleCreatePerson,
  };
}
